<template>
  <div class="patient-overview-knowledge-timeline pb-3">
    <v-row class="v-knowledge-timeline">
      <!----------------------------------- SELECTED VIDEO CATEGORIES --------------------------------------->
      <v-col cols="12" lg="3" class="flex-grow-1 pt-0 py-lg-0">
        <v-card class="border py-5 px-3" height="100%">
          <label-picker
            :selectAttrs="{ class: 'v-knowledge-timeline__select' }"
            v-model="selectedVideoCategories"
            :labels="videoCategories"
            :input-label="$ucfirst($t('categories'))"
            item-text="name"
            :all-labels-string="$ucfirst($t('knowledge.questions.startmoving.expert.whobenefits.answers.all', { catalog: 'content'}))"
            showListIcons
          />
        </v-card>
      </v-col>
      <!-------------------------------------- VIDEO CONTENT TIMELINE --------------------------------------->
      <v-col cols="12" lg="9" class="knowledge-timeline py-lg-0">
        <v-card class="border knowledge-timeline-container">
          <v-row class="pa-8">
            <v-col align-self="center" cols="12" md="6">
              <h3>{{ $ucfirst($t("timeline")) }}</h3>
            </v-col>
            <v-col>
              <h6 class="text-label">{{ $ucfirst($t("general.from", { catalog: 'app_mobile' })) }}</h6>
              <date-picker
                v-model="videoStartDate"
                :datePickerAttr="{
                  min: this.moment(visit1Date).format('YYYY-MM-DD'),
                  max: this.moment(videoEndDate).format('YYYY-MM-DD'),
                }"
                :disabledDateMessage="$ucfirst($t('validation.invalidDate.dateFromEnrolment'))"
                :hideMessageFromDate="this.moment(visit1Date).format('YYYY-MM-DD')"
                :hideMessageUntilDate="this.moment().format('YYYY-MM-DD')"
              ></date-picker>
            </v-col>
            <v-col>
              <h6 class="text-label">{{ $ucfirst($t("general.until", { catalog: 'app_mobile' })) }}</h6>
              <date-picker
                v-model="videoEndDate"
                alignRight
                :datePickerAttr="{
                  min: this.moment(videoStartDate).format('YYYY-MM-DD'),
                  max: this.moment().format('YYYY-MM-DD'),
                }"
                :disabledDateMessage="$ucfirst($t('validation.invalidDate.dateFromEnrolment'))"
                :hideMessageFromDate="this.moment(visit1Date).format('YYYY-MM-DD')"
                :hideMessageUntilDate="this.moment().format('YYYY-MM-DD')"
              ></date-picker>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pt-0">
              <timeline
                :active-index.sync="activeIndexTimeline"
                :selected-key.sync="selectedKeyTimeline"
                :timelineItems="timelineItems"
                :page-size="
                $vuetify.breakpoint.smAndDown
                ? 1
                : $vuetify.breakpoint.mdAndDown
                ? 3
                : 5
                "
                :column-width="$vuetify.breakpoint.smAndDown ? 100 : 150"
              />
              <div class="v-knowledge-timeline__activeyear">
                {{ activeYear }}
              </div>
            </v-col>
          </v-row>
          <v-col class="included">
            <div v-if="selectedKeyTimeline !== null">
              <v-card class="v-knowledge-timeline__videocontent border">
                <v-row style="height: 250px">
                  <v-col
                    cols="4"
                    align-self="center"
                    class="v-knowledge-timeline__videocontent__selected-date"
                  >
                    <p class="month">
                      {{ selectedMonthFull }}
                    </p>
                    <p class="year">
                      {{ selectedYear }}
                    </p>
                  </v-col>
                  <v-col
                    cols="8"
                    class="v-knowledge-timeline__videocontent__data"
                  >
                    <documents-per-category
                      :sortedPatientDocuments="sortedPatientDocuments"
                      :showDate="true"
                      :hideIcons="true"
                      :eventsInfo="selectedPatientDocumentsEvents"
                      :grouped="false"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </div>
            <div v-else>
              <v-card
                class="v-knowledge-timeline__videocontent clear-box pa-5 bg-color-coro-lightgray clear-box border"
              >
                <span
                  v-html="$ucfirst($t('knowledge.clickmonthinfo'))"
                ></span>
              </v-card>
            </div>
          </v-col>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import gql from "graphql-tag";
import Moment from "moment";
import { extendMoment } from "moment-range";
import Timeline from "../../../components/Parts/Timeline/Timeline";
import DatePicker from "../../../components/Parts/DatePicker";
import { debounce } from "debounce";
import { uniqueValues } from "../../../../../helper/arrays";
import DocumentsPerCategory from "../../../components/Parts/DocumentsPerCategory";
import LabelPicker from "../../../components/Parts/LabelPicker";
import PatientDocument from "../../../../../model/PatientDocument";
import { mapGetters } from 'vuex';

/**
 * Component PatientOverviewKnowledgeTimeline.vue
 *
 * @module PatientOverviewKnowledgeTimeline
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.8] app/src/APP_DASHBOARD/templates/views/Patient/Knowledge/PatientOverviewKnowledgeTimeline.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

const moment = extendMoment(Moment);

export default {
  name: "PatientOverviewKnowledgeTimeline",
  computed: {
    ...mapGetters({
      patientId: 'patientSummary/id',
      lastSDMEncounterDate: 'patientSummary/lastCompletedSDMEncounterDate'
    }),
    moment: () => moment,

    visit1Date() {
      const v1 = this.$store.getters["patientSummary/visit1Date"];
      return v1 ? moment(v1) : moment();
    },

    activeYear: function () {
      return this.timelineItems.length &&
        this.timelineItems[this.activeIndexTimeline]
        ? this.timelineItems[this.activeIndexTimeline].key.substring(0, 4)
        : null;
    },

    selectedYear: function () {
      if (this.selectedKeyTimeline !== null)
        return this.selectedKeyTimeline.substr(0, 4);

      return null;
    },

    selectedMonthFull: function () {
      if (this.selectedKeyTimeline !== null)
        return this.$ucfirst(
          this.$t("date.MMMM-" + this.selectedKeyTimeline.substr(5, 2))
        );

      return null;
    },

    selectedTimelineItem: function () {
      if (this.selectedKeyTimeline === null) return null;

      const timelineItems = this.timelineItems.filter(
        (entry) => entry.key === this.selectedKeyTimeline
      );

      return timelineItems.length ? timelineItems[0] : null;
    },

    sortedPatientDocuments: function () {
      let sorted = {};

      if (this.selectedPatientDocuments.length) {
        this.selectedPatientDocuments.forEach((doc) => {
          const foundCatObj = doc.document.mainCategory;
          const foundCat = foundCatObj ? foundCatObj.key : "";

          if (foundCat != "") {
            if (!sorted[foundCat]) {
              sorted[foundCat] = {
                category: foundCatObj,
                docs: {},
              };
            }

            sorted[foundCat].docs[doc.patientDocumentId] = doc;

            if (this.selectedTimelineItem) {
              doc.seen = !!this.selectedTimelineItem.line[0].patientDocuments[
                doc.patientDocumentId
              ];
              doc.documentTime = this.selectedTimelineItem.line[1].patientDocuments[
                doc.patientDocumentId
              ];
            }
          }
        });
      }

      return Object.values(sorted);
    },
  },
  data: function () {
    return {
      videoCategories: [],
      selectedVideoCategories: [],
      videoStartDate: moment().format("YYYY-MM-DD"),
      videoEndDate: moment().format("YYYY-MM-DD"),
      timelineItems: [],
      selectedPatientDocuments: [],
      loadingTimelineInformation: false,
      activeIndexTimeline: 0,
      selectedKeyTimeline: null,
      setInitialCategories: true,
      docsWithMultipleSentEvents: {},
      selectedPatientDocumentsEvents: {}
    };
  },
  watch: {
    videoEndDate(val) {
      if (
        this.videoEndDate &&
        this.videoStartDate &&
        this.videoEndDate < this.videoStartDate
      )
        this.videoStartDate = this.videoEndDate;

      this.selectedKeyTimeline = null;
      this.loadTimelineInformation();
    },
    videoStartDate(val) {
      if (
        this.videoEndDate &&
        this.videoStartDate &&
        this.videoStartDate > this.videoEndDate
      )
        this.videoEndDate = this.videoStartDate;

      this.selectedKeyTimeline = null;
      this.loadTimelineInformation();
    },
    patientId(val) {
      this.selectedKeyTimeline = null;
      this.loadTimelineInformation();
      this.loadSelectedPatientDocuments();
    },
    selectedKeyTimeline(val) {
      if (val) {
        this.selectedPatientDocumentsEvents = {}
        this.loadSelectedPatientDocuments();
        this.loadEvents();
      }
    },
  },
  methods: {
    loadSelectedPatientDocuments: function (ids) {
      this.selectedPatientDocuments = [];

      let patientDocumentIds = ids || [];

      if (this.selectedTimelineItem) {
        patientDocumentIds = uniqueValues(
          Object.keys(this.selectedTimelineItem.line[0].patientDocuments).concat(
            Object.keys(this.selectedTimelineItem.line[1].patientDocuments)
          )
        );
      }

      if (!patientDocumentIds.length) return;

      this.$app.apolloMobileClient
        .query({
          query: gql`
            query getSelectedPatientDocuments(
              $patientId: String!
              $patientDocumentIds: [String!]!
            ) {
              myPatientDocuments(
                functions: { function: patientId, parameters: [$patientId] }
                conditions: {
                  gate: and
                  inputs: [
                    {
                      field: patientDocumentId
                      operator: in
                      parameters: $patientDocumentIds
                    }
                  ]
                }
              ) {
                patientDocumentId
                timesOpened
                openDate

                document {
                  id
                  title

                  mainCategory {
                    id
                    key
                    name
                    type
                  }

                  categories {
                    id
                    key
                    name
                    type
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            patientDocumentIds: patientDocumentIds,
          },
        })
        .then((result) => {
          const docs = result.data.myPatientDocuments.map((pdData) => new PatientDocument(pdData));
          this.selectedPatientDocuments = docs;

          if (this.setInitialCategories && docs.length) {
            const selectedCategories = [];

            docs.forEach((item) => item.document.categories.forEach(cat => {
              const validCategory = this.videoCategories.find(item => item.key === cat.key);
              const uniqueCategory = !selectedCategories.some(item => item.key === cat.key);

              if (validCategory && uniqueCategory) {
                selectedCategories.push(validCategory)
              }
            }))

            this.selectedVideoCategories = selectedCategories
            this.setCategoryWatcher()
            this.setInitialCategories = false
          }
        });
    },

    loadEvents: function () {
      let startOfMonth = moment(this.selectedKeyTimeline).startOf('month').format('YYYY-MM-DD');
      let endOfMonth = moment(this.selectedKeyTimeline).add(1, 'months').startOf('month').format('YYYY-MM-DD');

      if (moment(endOfMonth).isAfter(moment())) {
        endOfMonth = moment().add(1, 'days').format('YYYY-MM-DD')
      }

      // load event info only if there are more than one 'sent' event per document in selected month
      const docIds = this.docsWithMultipleSentEvents[this.selectedKeyTimeline];
      if (!docIds || !docIds.length) return

      this.$app.apolloClient
        .query({
          query: gql`
            query getEventInfo(
              $patientId: String!
              $patientDocumentIds: [String!]!
              $startDate: String!
              $endDate: String!
            ) {
              contentPatientEvents(
                functions: { function: patientId, parameters: [$patientId] }
                conditions: {
                  gate: and
                  inputs: [
                    {field : eventType, operator: in, parameters : ["sent"]}
                    {field: patientDocumentId, operator: in, parameters: $patientDocumentIds}
                    {field : eventDate, operator: between parameters : [$startDate, $endDate]},
                  ]
                }
              ) {
                eventType
                createdAt
                patientDocument{
                  patientDocumentId
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            patientDocumentIds: docIds,
            startDate: startOfMonth,
            endDate: endOfMonth,
          },
        })
        .then((result) => {
          const events = result.data.contentPatientEvents
          // group by document ids
          const groupByDocId = events.reduce((group, event) => {
            let id = event.patientDocument.patientDocumentId;
            group[id] = group[id] || [];
            group[id].push(event.createdAt);
            return group;
          }, {});

          // count times 'sent' in one day
          const groupKeys = Object.keys(groupByDocId);
          groupKeys.forEach(key => {
            const uniqueNames = []
            const uniqueDays = []

            groupByDocId[key].forEach(item => {
              const date = moment(item).format('YYYY-MM-DD');

              if (!uniqueNames.includes(date)) {
                uniqueNames.push(date)
                uniqueDays.push({date, times: [item], count: 1, id: key});
              } else {
                uniqueDays.forEach(unique => {
                  if (unique.date === date) {
                    unique.count += 1
                    unique.times = [...unique.times, item]
                  }
                })
              }
            })
            groupByDocId[key] = uniqueDays
          })

          this.selectedPatientDocumentsEvents = groupByDocId
        });
    },

    setCategoryWatcher() {
      this.$watch('selectedVideoCategories', {
        handler : debounce(() => {
          this.selectedKeyTimeline = null;
          this.loadTimelineInformation();
        }, 1000)
      })
    },

    loadTimelineInformation: function () {
      if (this.loadingTimelineInformation) return;

      if (!(this.videoStartDate && this.videoEndDate))
        return (this.timelineItems = []);

      this.loadingTimelineInformation = true;

      let startDate = moment(this.videoStartDate);
      const endDate = moment(this.videoEndDate);
      const timeLineItems = [];

      while (startDate.isSameOrBefore(endDate, 'month')) {
        timeLineItems.push({
          key: startDate.format("YYYY-MM"),
          label: this.$ucfirst(this.$t("date.MMMM-" + startDate.format("MM"))),
          line: [
            {
              type: "opened",
              icon: "eva-eye-off-outline",
              text: null,
              patientDocuments: {},
            },
            {
              type: "sent",
              icon: "eva-video-outline",
              text: null,
              patientDocuments: {},
            },
          ],
        });

        startDate.add(1, "months");
      }

      this.timelineItems = timeLineItems;

      const timelineItemsLoadedFromDB = timeLineItems.slice();

      const videoEndDayPlus1Day = moment(this.videoEndDate);
      videoEndDayPlus1Day.add(1, "days");

      const variables = {
        patientId: this.patientId,
        videoStartDate: this.videoStartDate,
        videoEndDate: videoEndDayPlus1Day.format("YYYY-MM-DD"),
        selectedVideoCategories: this.selectedVideoCategories.length
          ? this.selectedVideoCategories.map((e) => e.key)
          : this.videoCategories.map((e) => e.key),
      };

      this.$app.apolloClient
        .query({
          query: gql`query getTimelineInformation(
            $patientId : String!,
            $videoStartDate : String!,
            $videoEndDate : String!,
            ${
              variables.selectedVideoCategories.length
                ? "$selectedVideoCategories : [String!]!,"
                : ""
            }
          ){
            contentPatientEventSummary(
              functions : {function : patientId, parameters : [$patientId]}
              conditions : {gate : and, inputs : [
                {field : eventType, operator: in, parameters : ["opened", "sent"]},
                {field : categoryKey, value : "EDUCATION"},
                {field : eventDate, operator: between parameters : [$videoStartDate, $videoEndDate]},
                ${
                  variables.selectedVideoCategories.length
                    ? "{field : categoryKey, operator: in, parameters : $selectedVideoCategories},"
                    : ""
                }
              ]}
              groups : [eventType, patientDocumentId, eventMonth]
            ){
              groupKey0
              groupKey1
              groupKey2
              count
              firstEventTime
            }
          }`,
          variables: variables,
        })
        .then((result) => {
          if (timeLineItems.length) {
            timeLineItems.forEach((timeLineItem) => {
              timeLineItem.line.forEach((block) => {
                block.text = 0;
              });
            });
          }

          const data = result.data.contentPatientEventSummary;

          // alias fields for more readable code
          data.forEach((dataEntry) => {
            dataEntry.eventType = dataEntry.groupKey0;
            dataEntry.patientDocumentId = dataEntry.groupKey1;
            dataEntry.eventMonth = dataEntry.groupKey2;
          });

          const _docsWithMultipleSentEvents = {}

          data.forEach((dataEntry) => {
            if (dataEntry.count) {
              const timeLineItem = timelineItemsLoadedFromDB.filter(
                (entry) => entry.key === dataEntry.eventMonth)[0];
              if (!timeLineItem) {
                return;
              }
              const block =
                timeLineItem.line[dataEntry.eventType === "opened" ? 0 : 1];

              if (dataEntry.eventType === "opened") {
                //ignore opened videos that haven't been sent by the case nurse during the same month
                if (
                  data.filter(
                    (compareEntry) =>
                      compareEntry.patientDocumentId ===
                        dataEntry.patientDocumentId &&
                      compareEntry.eventMonth === dataEntry.eventMonth &&
                      compareEntry.eventType === "sent"
                  ).length
                ) {
                  block.icon = "eva-eye-outline";
                  block.text = block.text + dataEntry.count;
                  block.patientDocuments[dataEntry.patientDocumentId] =
                    dataEntry.firstEventTime;
                }
              } else if (dataEntry.eventType === "sent") {
                block.text = block.text + dataEntry.count;
                block.patientDocuments[dataEntry.patientDocumentId] =
                  dataEntry.firstEventTime;

                // save those document ids that have more than one sent event - seperate by months
                if (dataEntry.count > 1) {
                  const month = dataEntry.groupKey2;
                  const eventsPerMonth = _docsWithMultipleSentEvents[month];

                  if (eventsPerMonth && eventsPerMonth.length) {
                    _docsWithMultipleSentEvents[month] = [...eventsPerMonth, dataEntry.patientDocumentId]
                  } else {
                    _docsWithMultipleSentEvents[month] = [dataEntry.patientDocumentId]
                  }
                }
              }
            }
          });

          this.docsWithMultipleSentEvents =_docsWithMultipleSentEvents;
          this.timelineItems = timelineItemsLoadedFromDB;
          this.loadingTimelineInformation = false;

          if (this.setInitialCategories) {
            const allIds = data
              .filter(item => item.eventType === 'sent')
              .map(item => item.patientDocumentId);
            this.loadSelectedPatientDocuments(allIds)
          }
        });
    },

    selectVideoCategories: function (keys) {
      const selectedVideoCategories = [];

      for (let i = 0; i < keys.length; i++)
        for (let j = 0; j < this.videoCategories.length; j++)
          if (this.videoCategories[j].key === keys[i])
            selectedVideoCategories.push(this.videoCategories[j]);

      this.selectedVideoCategories = selectedVideoCategories;
    },

    removeVideoCategory: function (key) {
      for (let i = 0; i < this.selectedVideoCategories.length; i++) {
        if (this.selectedVideoCategories[i].key === key) {
          this.selectedVideoCategories.splice(i, 1);
          return;
        }
      }
    },

    loadVideoCategories: function () {
      this.$app.knowledgeManager
        .getKnowledgeCategories(this.patientId)
        .then((categories) => (this.videoCategories = categories.map(item => item.category)));
    },
  },
  mounted: function () {
    const start = this.lastSDMEncounterDate;
    if (start.isValid()) {
      this.videoStartDate = start.format("YYYY-MM-DD")
    }
    this.loadVideoCategories();
    this.loadTimelineInformation();
  },
  components: {
    LabelPicker,
    DatePicker,
    Timeline,
    DocumentsPerCategory,
  },
};
</script>

<style lang="scss">
@import "../../../../../scss/colors";

// mobile first
.v-knowledge-timeline__select {
  max-width: 80%;
}

.v-knowledge-timeline__videocontent {
  border-radius: 16px;
  height: 250px;
  overflow: hidden;

  div.border {
    background-color: #ffffff;
  }
  & .row {
    margin-right: 0;
    margin-left: 0;
  }
}

.v-knowledge-timeline__videocontent__selected-date {
  text-align: center;
  font-weight: 600;
  align-items: center;
  justify-content: center;

  .year {
    font-size: 1.3rem;
    line-height: 1rem;
  }
  .month {
    font-size: 0.9rem;
    line-height: 0.5rem;
  }
}

.v-knowledge-timeline__videocontent__data {
  border-left: 1px solid map-get($coroColors, lightgray2);
  padding: 0 !important;
}

.v-knowledge-timeline__activeyear {
  margin: 0 30px;
  font-weight: bold;
  font-size: 1.1em;
}

.v-knowledge-timeline__date {
  float: right;
  margin-left: 10px;
}

// mobile screens

// tablet screens
.v-application.md-up {
  .v-knowledge-timeline__activeyear {
    margin: 0 56px;
  }
}

// very large screens only
.v-application.xl-only {
  .v-knowledge-timeline__date {
    max-width: 300px;
  }
}

.knowledge-timeline {
  overflow: hidden;
}

.knowledge-timeline-container {
  overflow: hidden;
}

// -------------------------------------------------- BEM modifiers ------------------------------------------------
</style>
